import { useEffect, useMemo, useState } from "react";
import { Modal, Table, Radio, Spin, Empty, Typography } from "antd";
import {
  buildFinancialMetricColumns,
  formatParticulars,
} from "../../helpers/helpers";

const { Text } = Typography;

const TITLE_BY_KEY = {
  financialEvaluation: "Financial Evaluation",
  financialSummary: "Financial Summary",
  ratios: "Ratios",
};

const getPreviewRows = (metricsData, uploadKey) => {
  if (!metricsData || !uploadKey) {
    return { mode: "table", rows: [], categories: {} };
  }

  if (uploadKey === "financialEvaluation") {
    const categories =
      metricsData?.Business?.["Financial Evaluation"] || {};
    return { mode: "categories", rows: [], categories };
  }

  const enterpriseMetrics =
    metricsData?.Enterprise || metricsData?.Enterpise || {};

  if (uploadKey === "financialSummary") {
    return {
      mode: "table",
      rows: enterpriseMetrics?.["Financial Summary"] || [],
      categories: {},
    };
  }

  if (uploadKey === "ratios") {
    return {
      mode: "table",
      rows: enterpriseMetrics?.Ratios || [],
      categories: {},
    };
  }

  return { mode: "table", rows: [], categories: {} };
};

const FinancialMetricsPreviewModal = ({
  open,
  onClose,
  loading,
  metricsData,
  uploadKey,
}) => {
  const preview = useMemo(
    () => getPreviewRows(metricsData, uploadKey),
    [metricsData, uploadKey]
  );

  const categoryNames = Object.keys(preview.categories || {});
  const categoriesKey = categoryNames.join("\0");
  const [selectedCategory, setSelectedCategory] = useState(
    categoryNames[0] || ""
  );

  useEffect(() => {
    const names = categoriesKey ? categoriesKey.split("\0") : [];
    if (!names.length) {
      setSelectedCategory("");
      return;
    }
    setSelectedCategory((prev) => (names.includes(prev) ? prev : names[0]));
  }, [categoriesKey]);

  const tableRows =
    preview.mode === "categories"
      ? preview.categories?.[selectedCategory] || []
      : preview.rows || [];

  const columns = buildFinancialMetricColumns(tableRows, (text) => (
    <span style={{ color: "#40566D" }}>{formatParticulars(text)}</span>
  ));

  const hasData =
    preview.mode === "categories"
      ? categoryNames.length > 0
      : (preview.rows || []).length > 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      centered
      title={TITLE_BY_KEY[uploadKey] || "Financial Metrics"}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {!loading && !hasData ? (
          <Empty description="No uploaded metrics to preview" />
        ) : (
          <div>
            {preview.mode === "categories" && categoryNames.length > 0 && (
              <Radio.Group
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{ marginBottom: 16 }}
              >
                {categoryNames.map((category) => (
                  <Radio key={category} value={category}>
                    {category}
                  </Radio>
                ))}
              </Radio.Group>
            )}

            {preview.mode === "categories" && !selectedCategory ? (
              <Text type="secondary">Select a category to view metrics.</Text>
            ) : (
              <Table
                dataSource={tableRows}
                columns={columns}
                rowKey={(row, index) =>
                  `${row?.particulars || "row"}-${index}`
                }
                bordered
                pagination={false}
                scroll={{ x: "max-content" }}
                size="small"
              />
            )}
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default FinancialMetricsPreviewModal;
